import React, { useState } from "react";
import { Avatar, Button, Dropdown, MenuProps, App } from "antd";
import Link from "next/link";
import {
    UserOutlined,
    DownloadOutlined,
    GlobalOutlined,
    LoginOutlined,
    QuestionCircleOutlined,
    BugOutlined,
} from "@ant-design/icons";
import { useAuth } from "@/context/AuthContext";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { usePWA } from "@/hooks/usePWA";
import i18nConfig from "@/i18nConfig";
import styles from "@/ui/page.module.css";
import KeyboardShortcutsModal from "./KeyboardShortcutsModal";
import ReportProblemModal from "./ReportProblemModal";

interface AppHeaderProps {
    title?: string;
    headerContent?: React.ReactNode;
}

const AppHeader: React.FC<AppHeaderProps> = ({ title, headerContent }) => {
    const { isLoggedIn, user, logout } = useAuth();
    const router = useRouter();
    const { t, i18n } = useTranslation();
    const { isInstallable, isInstalled, installApp } = usePWA(false);
    const { modal } = App.useApp();
    const currentLocale = i18n.language;
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);

    const isDevelopment = process.env.NODE_ENV === "development";
    const shouldShowDownload = !isDevelopment && !isInstalled;

    const handleLogout = () => {
        // logout reports its own errors and never rejects
        void logout(() => {
            router.replace("/");
        });
    };

    const handleDownloadApp = () => {
        if (isInstallable) {
            // installApp catches and logs its own errors
            void installApp();
        } else {
            const message = isDevelopment
                ? t("modal.pwaInstall.devMessage")
                : t("modal.pwaInstall.unavailableMessage");
            modal.info({
                title: t("modal.pwaInstall.title"),
                content: message,
            });
        }
    };

    const handleLanguageChange = (newLocale: string) => {
        // Set cookie for next-i18n-router
        const days = 30;
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        document.cookie = `NEXT_LOCALE=${newLocale};expires=${date.toUTCString()};path=/`;

        // Redirect to the new locale path
        const currentURL =
            window.location.pathname +
            window.location.search +
            window.location.hash;
        if (
            currentLocale === i18nConfig.defaultLocale &&
            !i18nConfig.prefixDefault
        ) {
            router.push("/" + newLocale + currentURL);
        } else {
            router.push(
                currentURL.replace(`/${currentLocale}`, `/${newLocale}`),
            );
        }

        router.refresh();
    };

    const handleLogin = () => {
        router.push("/login");
    };

    // Language submenu items
    const languageItems: MenuProps["items"] = [
        {
            key: "lang-en",
            label: t("language.en"),
            onClick: () => handleLanguageChange("en"),
        },
        {
            key: "lang-hr",
            label: t("language.hr"),
            onClick: () => handleLanguageChange("hr"),
        },
    ];

    // Menu items for logged-in users
    const loggedInMenuItems: MenuProps["items"] = [
        shouldShowDownload
            ? {
                  key: "download",
                  label: t("button.downloadApp"),
                  icon: <DownloadOutlined />,
                  onClick: handleDownloadApp,
              }
            : null,
        {
            key: "language",
            label: t("header.language"),
            icon: <GlobalOutlined />,
            children: languageItems,
        },
        {
            key: "shortcuts",
            label: t("header.keyboardShortcuts"),
            icon: <QuestionCircleOutlined />,
            onClick: () => setShortcutsOpen(true),
        },
        {
            // Logged-in only: the API refuses anonymous reports.
            key: "report",
            label: t("report.menu"),
            icon: <BugOutlined />,
            onClick: () => setReportOpen(true),
        },
        {
            type: "divider" as const,
        },
        {
            key: "profile",
            label: t("header.openProfile"),
            disabled: true,
        },
        {
            key: "logout",
            label: t("header.logout"),
            onClick: handleLogout,
        },
    ].filter((item): item is NonNullable<typeof item> => item !== null);

    // Menu items for logged-out users
    const loggedOutMenuItems: MenuProps["items"] = [
        shouldShowDownload
            ? {
                  key: "download",
                  label: t("button.downloadApp"),
                  icon: <DownloadOutlined />,
                  onClick: handleDownloadApp,
              }
            : null,
        {
            key: "language",
            label: t("header.language"),
            icon: <GlobalOutlined />,
            children: languageItems,
        },
        {
            key: "shortcuts",
            label: t("header.keyboardShortcuts"),
            icon: <QuestionCircleOutlined />,
            onClick: () => setShortcutsOpen(true),
        },
        {
            type: "divider" as const,
        },
        {
            key: "login",
            label: t("header.login"),
            icon: <LoginOutlined />,
            onClick: handleLogin,
        },
    ].filter((item): item is NonNullable<typeof item> => item !== null);

    const menuItems = isLoggedIn ? loggedInMenuItems : loggedOutMenuItems;

    return (
        <>
            <Link
                href="/"
                className={styles.title}
                style={{ display: "flex", alignItems: "center" }}
            >
                <img
                    src="/blocks-code.svg"
                    alt={title ?? "blocks code"}
                    style={{ height: 24, marginRight: 8 }}
                    onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display =
                            "none";
                    }}
                />
            </Link>
            <div style={{ width: "20px" }}></div>
            <div style={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                {headerContent}
            </div>
            {isLoggedIn && user ? (
                <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
                    <Avatar
                        src={
                            undefined //user.avatar_image || undefined
                        }
                        alt={user.username}
                        style={{ cursor: "pointer" }}
                    >
                        {
                            user.username.toUpperCase()
                            // !user.avatar_image &&
                            // user.username.toUpperCase()
                        }
                    </Avatar>
                </Dropdown>
            ) : (
                <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
                    <Button
                        ghost
                        icon={<UserOutlined />}
                        style={{ cursor: "pointer" }}
                    />
                </Dropdown>
            )}
            <KeyboardShortcutsModal
                open={shortcutsOpen}
                onClose={() => setShortcutsOpen(false)}
            />
            {isLoggedIn && (
                <ReportProblemModal
                    open={reportOpen}
                    onClose={() => setReportOpen(false)}
                />
            )}
        </>
    );
};

export default AppHeader;
